import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import cx from 'classnames';
import { StyledModal } from 'components/shared';
import useOnClickOutside from 'utils/hooks/useOnClickOutside';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import breakpoints from 'utils/breakpoints';
import { colors } from 'utils/tailwindColors';
import trackingEvents from 'utils/trackingEvents';
import { track } from 'utils/tracking';

const ModalContent = styled.div`
  width: 20.625rem;
  border-radius: 0.625rem;
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.4);

  @media ${breakpoints.md} {
    width: 36.25rem;
  }
`;

const Heading = styled.div`
  max-width: 15rem;
  padding-top: 2.85rem;
  letter-spacing: -0.5px;
  line-height: 2.625rem;

  @media ${breakpoints.md} {
    max-width: 26rem;
    padding-top: 4.8rem;
  }
`;

const ButtonsContainer = styled.div`
  padding-bottom: 1.75rem;

  @media ${breakpoints.md} {
    margin-top: 1.45rem;
    padding-bottom: 3.5rem;
  }
`;

const AppointmentButton = styled.button`
  min-width: 0;
  height: 3.75rem;
  border-radius: 0.625rem;
  border: 1.5px solid ${colors['action-blue']};
  background: transparent;
  color: ${colors['action-blue']};
  font-family: 'TradeGothic-BoldCondTwenty';
  font-size: 1rem;
  letter-spacing: 0.04em;
  padding: 0 1.5rem;
  text-transform: uppercase;

  &:hover {
    background: ${colors['action-blue']};
    color: ${colors.white};
  }

  @media ${breakpoints.md} {
    width: 10rem;
  }
`;

const ScheduleAnAppointmentModal = ({
  isOpen = true,
  onClose = () => {},
  fields,
}) => {
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const buttonsContainerRef = useRef();
  const modalRef = useRef();
  const { title, firstCtaLink, secondCtaLink } = fields;

  useOnClickOutside(modalRef, (event) => {
    if (!isOpen) return;
    onClose(event);
  });

  useEffect(() => {
    setTimeout(() => {
      if (buttonsContainerRef.current) {
        const element = buttonsContainerRef.current;
        element.children[0].focus();
      }
    }, 2000);
  }, []);

  const trackEventId = trackingEvents.BookAppointment;
  return (
    <StyledModal
      isOpen={isOpen}
      onBackgroundClick={onClose}
      onEscapeKeydown={onClose}
    >
      <div
        className='flex justify-center items-center bg-black-faded-light w-screen h-screen'
        aria-modal='true'
        role='dialog'
      >
        <ModalContent
          className='flex flex-col items-center bg-white'
          ref={modalRef}
        >
          <Heading className='w-full font-trade-gothic-20 text-mattamy-blue text-4xl text-center'>
            {title}
          </Heading>
          <ButtonsContainer
            className={cx('items-center', {
              'flex flex-col mt-8 w-full px-6': isMobile,
              'flex justify-center mt-10': !isMobile,
            })}
            ref={buttonsContainerRef}
          >
            {firstCtaLink && firstCtaLink.text && firstCtaLink.href && (
              <AppointmentButton
                className='mb-5 md:mr-3 md:mb-0'
                onClick={() => {
                  window.dataLayer.push({ event: 'schedule-appointment' });
                  if (trackEventId) {
                    track({ eventId: trackEventId });
                  }
                  window.Calendly.initPopupWidget({
                    url: firstCtaLink.href,
                  });
                }}
                tabIndex={0}
              >
                {firstCtaLink.text}
              </AppointmentButton>
            )}
            {secondCtaLink && secondCtaLink.text && secondCtaLink.href && (
              <AppointmentButton
                className='mb-5 md:ml-3 md:mb-0'
                onClick={() => {
                  window.dataLayer.push({
                    event: 'schedule-appointment-virtual',
                  });
                  if (trackEventId) {
                    track({ eventId: trackEventId });
                  }
                  window.Calendly.initPopupWidget({
                    url: secondCtaLink.href,
                  });
                }}
                tabIndex={0}
              >
                {secondCtaLink.text}
              </AppointmentButton>
            )}
          </ButtonsContainer>
        </ModalContent>
      </div>
    </StyledModal>
  );
};

ScheduleAnAppointmentModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onInPersonClick: PropTypes.func,
  onVirtualClick: PropTypes.func,
};

export default ScheduleAnAppointmentModal;
